import { STRINGS } from 'config';
import _flattenDeep from 'lodash/flattenDeep';
import _uniqBy from 'lodash/uniqBy';
import { convertVitalsToEntries, SnomedEntry } from 'model/toolbar/snomed';
import React, { useEffect } from 'react';
import { fetchAiSnomedCodes } from 'store/action/snomed';
import { useAppDispatch, useAppState } from 'store/configureStore';
import { AiSnomedEntry } from 'store/reducer/snomed';
import SnomedCodeListItem from './SnomedItem';
import { Header, SnomedListWrapper } from './styles';

function checkToRemoveDisorderCodes(clinicalSystemName: string | null, aiSnomedCodes: AiSnomedEntry[]) {
  if (clinicalSystemName === 'EMIS') {
    return aiSnomedCodes?.filter(code => !code?.info.toLowerCase().includes('(disorder)'));
  }
  return aiSnomedCodes;
}

const {
  REQUEST_STATUS: { SUCCEEDED, PENDING },
  TEMPLATE: { EMIS_WEB_TEMPLATE },
} = STRINGS;

const Snomed: React.FC = () => {
  const {
    app: { clinicalSystemName, selectedPractice },
    snomed: { vitals, selectedSnomed, aiSnomedCodes, aiSnomedFetchStatus },
    summary: { currentSummary, consultationId, finishedSummaryRequest, hasEditsMadeToSummary, error },
    featureFlags: { AI_SNOMED_CODES_ENABLED },
    templates: {
      template: { name },
    },
  } = useAppState();
  const dispatch = useAppDispatch();
  const vitalEntries: SnomedEntry[] = convertVitalsToEntries(vitals, clinicalSystemName);
  const selectedPracticeId = selectedPractice?.id;
  useEffect(() => {
    const validSummary = currentSummary && !error;
    if (AI_SNOMED_CODES_ENABLED?.includes(clinicalSystemName || '') || clinicalSystemName === null) {
      if (aiSnomedCodes === null && validSummary && finishedSummaryRequest && !hasEditsMadeToSummary) {
        if (selectedPracticeId) {
          fetchAiSnomedCodes(dispatch, { summary: currentSummary, consultationId: consultationId }, selectedPracticeId);
        }
      }
    }
  }, [
    dispatch,
    aiSnomedCodes,
    consultationId,
    currentSummary,
    finishedSummaryRequest,
    clinicalSystemName,
    AI_SNOMED_CODES_ENABLED,
    hasEditsMadeToSummary,
    error,
    selectedPracticeId,
  ]);

  const handleToggle = (code: string) => {
    dispatch({
      type: STRINGS.REDUCERS.SNOMED.TOGGLE_SELECTED_SNOMED,
      payload: { code },
    });
  };
  const hasSnomedCodes = Boolean(aiSnomedCodes?.length) || Boolean(vitalEntries.length);
  const showGeneratingAiSnomedCodeMessage = aiSnomedCodes === null && aiSnomedFetchStatus === PENDING;
  const showNoAiSnomedCodesFoundMessage = aiSnomedCodes?.length === 0 && aiSnomedFetchStatus === SUCCEEDED;
  const showGenericSnomedMessage =
    !hasSnomedCodes && !showGeneratingAiSnomedCodeMessage && !showNoAiSnomedCodesFoundMessage;
  const aiSnomedCodesToUse = () => {
    if (name === EMIS_WEB_TEMPLATE) {
      return checkToRemoveDisorderCodes(clinicalSystemName, aiSnomedCodes || []);
    }
    return aiSnomedCodes;
  };
  const uniqSnomedCodes = _uniqBy(_flattenDeep(aiSnomedCodesToUse()), 'code');
  const hasCodesToShow = uniqSnomedCodes.length !== 0 && aiSnomedFetchStatus === SUCCEEDED;
  return (
    <>
      <Header>SNOMED Codes</Header>
      {showGenericSnomedMessage && (
        <p style={{ fontSize: '14px' }}>As relevant SNOMED codes are generated they will appear here.</p>
      )}
      {showGeneratingAiSnomedCodeMessage && (
        <p style={{ fontSize: '14px' }}>Huma intelligence is generating additional relevant SNOMED codes…</p>
      )}
      {showNoAiSnomedCodesFoundMessage && (
        <p style={{ fontSize: '14px' }}>No additional codes suggested by Huma intelligence.</p>
      )}
      <SnomedListWrapper data-automation="SnomedListWrapper">
        {hasCodesToShow &&
          uniqSnomedCodes?.map((entry: SnomedEntry) => (
            <SnomedCodeListItem
              key={entry.code}
              snomedCode={entry}
              isToggled={selectedSnomed.includes(entry.code)}
              handleToggle={handleToggle}
              isAiSnomed
            />
          ))}
        {vitalEntries.map(entry => (
          <SnomedCodeListItem
            key={entry.code}
            snomedCode={entry}
            isToggled={selectedSnomed.includes(entry.code)}
            handleToggle={handleToggle}
            isAiSnomed={false}
          />
        ))}
      </SnomedListWrapper>
    </>
  );
};

export default Snomed;
